// EventForm.js
import React, { useState } from 'react';
import {
  Box, TextField, Button, Typography, IconButton, Divider,
  Paper, Alert, MenuItem, FormControl, InputLabel, Select,
  FormControlLabel, Switch, useTheme, Chip, Stack, OutlinedInput,
  Checkbox, ListItemText
} from '@mui/material';
import { Close, Save } from '@mui/icons-material';
import { useAuditFirestore } from '../../hooks/useAuditFirestore';
import { useAuth } from '../../hooks/useAuth';
import { useCollection } from '../../hooks/useCollection';
import { EVENT_TYPES, EVENT_TYPE_LABELS, EVENT_TYPE_COLORS, createEventItem } from '../../utils/models/calendarModel';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 280,
    },
  },
};

const EventForm = ({ event, onSave, onCancel }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const { add, update } = useAuditFirestore('calendarEvents');
  const { data: users } = useCollection('users');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toISOString().slice(0, 16);
  };

  // Determinar assignedTo inicial: si es edición usa el array existente, si es nuevo incluye al usuario actual
  const getInitialAssignedTo = () => {
    if (event?.id) {
      return event.assignedTo || [];
    }
    return user?.uid ? [user.uid] : [];
  };

  const [formData, setFormData] = useState({
    title: event?.title || '',
    description: event?.description || '',
    eventType: event?.eventType || EVENT_TYPES.MEETING,
    startDate: formatDateForInput(event?.startDate) || formatDateForInput(new Date()),
    endDate: formatDateForInput(event?.endDate) || formatDateForInput(new Date(Date.now() + 3600000)),
    allDay: event?.allDay || false,
    color: event?.color || EVENT_TYPE_COLORS[EVENT_TYPES.MEETING],
    createdBy: event?.createdBy || user.uid,
    createdByName: event?.createdByName || user.displayName || user.email,
    assignedTo: getInitialAssignedTo(),
  });

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'allDay' ? checked : value
    }));
    if (error) setError('');
  };

  const handleAssignedToChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      assignedTo: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleEventTypeChange = (e) => {
    const newType = e.target.value;
    setFormData(prev => ({
      ...prev,
      eventType: newType,
      color: EVENT_TYPE_COLORS[newType]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title) {
      setError('El título es obligatorio');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = {
        ...formData,
        updatedAt: new Date().toISOString(),
      };
      if (event?.id) {
        await update(event.id, data);
      } else {
        await add({
          ...data,
          createdAt: new Date().toISOString(),
        });
      }
      onSave();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.background.paper,
      height: { xs: 40, sm: 44 },
    },
    "& .MuiInputLabel-root": {
      backgroundColor: theme.palette.background.paper,
      paddingInline: "4px",
    },
  };

  const sectionTitleSx = {
    fontWeight: 700,
    fontSize: { xs: 14, sm: 15 },
    mb: { xs: 1.5, sm: 2 },
    mt: { xs: 3, sm: 4 },
    color: theme.palette.primary.main,
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        borderRadius: { xs: "16px", sm: "24px" },
        border: "1px solid",
        borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : '#e2e8f0',
        background: theme.palette.background.paper,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: { xs: 3, sm: 4 } }}>
        <Box>
          <Typography variant="h5" fontWeight={800} sx={{ fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" } }}>
            {event?.id ? "Editar evento" : "Nuevo evento"}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
            Programa un evento en el calendario
          </Typography>
        </Box>
        <IconButton
          onClick={onCancel}
          size="small"
          sx={{ 
            background: theme.palette.background.paper, 
            border: "1px solid",
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : '#e2e8f0',
          }}
        >
          <Close sx={{ fontSize: { xs: 20, sm: 24 } }} />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: { xs: 2, sm: 3 }, borderRadius: "12px", fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Typography sx={sectionTitleSx}>Información del evento</Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 2, sm: 2.5 }, width: "100%" }}>
          <TextField
            fullWidth
            size="small"
            label="Título"
            name="title"
            value={formData.title}
            onChange={handleChange}
            sx={fieldSx}
            required
            inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
          />

          <TextField
            fullWidth
            size="small"
            label="Descripción"
            name="description"
            value={formData.description}
            onChange={handleChange}
            multiline
            rows={3}
            inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "16px",
                background: theme.palette.background.paper,
              },
            }}
          />

          <Box sx={{ display: "flex", gap: { xs: 2, sm: 2.5 }, flexWrap: "wrap" }}>
            <FormControl fullWidth size="small" sx={{ flex: 1, minWidth: { xs: "100%", sm: "260px" } }}>
              <InputLabel sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>Tipo de evento</InputLabel>
              <Select
                name="eventType"
                value={formData.eventType}
                onChange={handleEventTypeChange}
                label="Tipo de evento"
                sx={{ borderRadius: "12px", background: theme.palette.background.paper, height: { xs: 40, sm: 44 }, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
              >
                {Object.entries(EVENT_TYPE_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value} sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: EVENT_TYPE_COLORS[value] }} />
                      {label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: "260px" }, display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    name="allDay"
                    checked={formData.allDay}
                    onChange={handleChange}
                  />
                }
                label={<Typography sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>Todo el día</Typography>}
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: { xs: 2, sm: 2.5 }, flexWrap: "wrap" }}>
            <TextField
              fullWidth
              size="small"
              label="Fecha y hora inicio"
              name="startDate"
              type="datetime-local"
              value={formData.startDate}
              onChange={handleChange}
              sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "260px" } }}
              InputLabelProps={{ shrink: true, style: { fontSize: { xs: "0.75rem", sm: "0.85rem" } } }}
              inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
            />
            <TextField
              fullWidth
              size="small"
              label="Fecha y hora fin"
              name="endDate"
              type="datetime-local"
              value={formData.endDate}
              onChange={handleChange}
              sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "260px" } }}
              InputLabelProps={{ shrink: true, style: { fontSize: { xs: "0.75rem", sm: "0.85rem" } } }}
              inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
            />
          </Box>
        </Box>

        {/* SECCIÓN DE ASIGNACIÓN */}
        <Typography sx={sectionTitleSx}>Asignación</Typography>

        <FormControl fullWidth size="small">
          <InputLabel id="assigned-to-label" sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>Asignado a</InputLabel>
          <Select
            labelId="assigned-to-label"
            id="assigned-to"
            multiple
            name="assignedTo"
            value={formData.assignedTo}
            onChange={handleAssignedToChange}
            input={<OutlinedInput label="Asignado a" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.length === 0 ? (
                  <Chip 
                    key="general" 
                    label="Evento general (visible para todos)" 
                    size="small"
                    sx={{ 
                      borderRadius: "8px",
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255,255,255,0.1)' 
                        : 'rgba(0,0,0,0.06)',
                      fontWeight: 500,
                    }} 
                  />
                ) : (
                  selected.map((userId) => {
                    const foundUser = users?.find(u => u.id === userId);
                    return (
                      <Chip 
                        key={userId} 
                        label={foundUser?.displayName || foundUser?.email || userId}
                        size="small"
                        sx={{ 
                          borderRadius: "8px",
                          fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          backgroundColor: userId === user.uid 
                            ? theme.palette.primary.main + '20'
                            : theme.palette.mode === 'dark' 
                              ? 'rgba(255,255,255,0.1)' 
                              : 'rgba(0,0,0,0.06)',
                          fontWeight: userId === user.uid ? 600 : 400,
                        }} 
                      />
                    );
                  })
                )}
              </Box>
            )}
            MenuProps={MenuProps}
            sx={{ 
              borderRadius: "12px", 
              background: theme.palette.background.paper,
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
              '& .MuiOutlinedInput-root': {
                borderRadius: "12px",
              }
            }}
          >
            <MenuItem value="" sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>
              <em>Seleccionar usuarios</em>
            </MenuItem>
            {users?.map((u) => (
              <MenuItem key={u.id} value={u.id} sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>
                <Checkbox checked={formData.assignedTo.indexOf(u.id) > -1} />
                <ListItemText 
                  primary={u.displayName || u.email} 
                  secondary={u.id === user.uid ? 'Tú' : u.email}
                  primaryTypographyProps={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                  secondaryTypographyProps={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {event?.id && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>
              Creado por: {formData.createdByName}
            </Typography>
          </Box>
        )}

        <Divider sx={{ mt: { xs: 3, sm: 4 }, mb: { xs: 2, sm: 3 } }} />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: { xs: 1, sm: 2 }, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            sx={{ borderRadius: "12px", px: { xs: 2, sm: 3 }, height: { xs: 40, sm: 44 }, textTransform: "none", fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
            fullWidth={{ xs: true, sm: false }}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={<Save />}
            sx={{ borderRadius: "12px", px: { xs: 2, sm: 3 }, height: { xs: 40, sm: 44 }, boxShadow: "none", textTransform: "none", fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
            fullWidth={{ xs: true, sm: false }}
          >
            {loading ? "Guardando..." : event?.id ? "Actualizar" : "Crear evento"}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default EventForm;